import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpXhrBackend,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { BrowserModule } from '@angular/platform-browser';
import { Router, RouteReuseStrategy } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage-angular';
import { InputMaskModule } from '@ngneat/input-mask';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { LOCAL_STORAGE_ENGINE, NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import * as SentryAngular from '@sentry/angular';
import { ChatState } from './core/domains/chats/state/chat.state';
import { EventsOfMyGroupsState } from './core/domains/events/state/events-of-my-groups.state';
import { EditGroupState } from './core/domains/groups/state/edit-group.state';
import { ExploreGroupsState } from './core/domains/groups/state/explore-groups.state';
import { GroupContentState } from './core/domains/groups/state/group-content.state';
import { MyGroupsState } from './core/domains/groups/state/my-groups.state';
import { PendingInvitesState } from './core/domains/groups/state/pending-invites.state';
import { SearchGroupsState } from './core/domains/groups/state/search-groups.state';
import { MyGroupInvitationsState } from './core/domains/groups/state/my-group-invitations.state';
import { NotificationInboxState } from './core/domains/notifications/state/notification-inbox.state';
import { InviteUsersState } from './core/domains/groups/state/invite-users.state';
import { MessagingState } from '@smatchy/domains/message';
import { UserNotificationState } from './core/domains/user/state/user-notification.state';
import { UserLocationAccessState } from './core/domains/user/state/user-location-access.state';
import { FirebaseFactory, FirebaseService } from '@smatchy/ionic-auth';
import { BASE_PATH, Configuration } from '@smatchy/sdk';
import { environment } from '../environments/environment';
import { CameraAdapter } from './adapters/camera.adapter';
import { StripeAdapter } from './adapters/stripe.adapter';
import { PushNotificationAdapter } from './adapters/push-notification.adapter';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CreateGroupState } from './core';
import { AppErrorState } from './core/domains/app/state/app-error.state';
import { AppState } from './core/domains/app/state/app.state';
import { CreateEventState } from './core/domains/events/state/create-event.state';
import { DetailEventsState } from './core/domains/events/state/detail-events.state';
import { FilterEventsState } from './core/domains/events/state/filter-events.state';
import { MyEventsState } from './core/domains/events/state/my-events.state';
import { MyEventInvitationsState } from './core/domains/events/state/my-event-invitations.state';
import { SportEventDefinitionState } from './core/domains/events/state/sport-event-definition.state';
import { LocationAutocompleteState } from './core/domains/locations/state/location-autocomplete.state';
import { SportsState } from './core/domains/sports/state/sports.state';
import { AuthState } from './core/domains/user/state/auth.state';
import { UserLocationState } from './core/domains/user/state/user-location.state';
import { UserProfileState } from './core/domains/user/state/user-profile.state';
import { UserSportsState } from './core/domains/user/state/user-sports.state';
import { ActivityPreferencesState } from './core/domains/user/state/activity-preferences.state';
import { PaymentState } from './core/domains/payments/state/payment.state';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { AppVersionInterceptor } from './core/interceptors/app-version.interceptor';
import { NOTIFICATIONS_REPOSITORY } from './core/modules/users/notifications/notifications.repository';
import { NotificationsRepositoryImpl } from './core/modules/users/notifications/notifications.repository.impl';
import { LanguageService } from './core/services/language.service';
import { JWTService } from '@smatchy/ionic-auth';
import { TokenInterceptor } from './token.interceptor';
import './tracing';
import { PremiumRoleState } from './core/domains/premium-role';
import {
  FindProOrganizerRequestByUserState,
  ProOrganizerState,
  UpdateRequestState,
} from './core/domains/pro-organizer-request';
import { CgvState } from './core/domains/cgv';
import { StripeConnectState } from './core/domains/organizers/state/stripe-connect.state';
import { FilterPaidEventState } from './core/domains/events/state/filter-paid-event.state';
import { BalancesState, FilterEarningEventState, PayoutState } from './core/domains/organizers/state';

import { LocationAccuracy } from '@awesome-cordova-plugins/location-accuracy/ngx';
export function createTranslateLoader() {
  const http = new HttpClient(
    new HttpXhrBackend({
      build: () => new XMLHttpRequest(),
    }),
  );
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

function apiInitializer() {
  return environment.api;
}

const STATE_LOCAL_STORAGE_SAVED = [SportsState, AuthState, UserProfileState, SportEventDefinitionState, MessagingState];

const ALL_STATES = [
  ...STATE_LOCAL_STORAGE_SAVED,
  AppState,
  UserLocationState,
  UserSportsState,
  ActivityPreferencesState,
  LocationAutocompleteState,
  AppErrorState,
  GroupContentState,
  MyGroupsState,
  CreateGroupState,
  PendingInvitesState,
  EditGroupState,
  CreateEventState,
  FilterEventsState,
  MyEventsState,
  MyEventInvitationsState,
  DetailEventsState,
  ExploreGroupsState,
  EventsOfMyGroupsState,
  SearchGroupsState,
  ChatState,
  MessagingState,
  UserNotificationState,
  UserLocationAccessState,
  PremiumRoleState,
  ProOrganizerState,
  FindProOrganizerRequestByUserState,
  UpdateRequestState,
  PaymentState,
  CgvState,
  FilterPaidEventState,
  StripeConnectState,
  PayoutState,
  BalancesState,
  FilterEarningEventState,
  MyGroupInvitationsState,
  NotificationInboxState,
  InviteUsersState,
];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot({
      swipeBackEnabled: true,
      hardwareBackButton: true,
      scrollAssist: true,
      // scrollPadding
    }),
    AppRoutingModule,
    TranslateModule.forRoot({
      defaultLanguage: 'fr',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
      },
    }),
    GoogleMapsModule,
    InputMaskModule.forRoot({ inputSelector: 'input', isAsync: true }),
    NgxsModule.forRoot(ALL_STATES),
    NgxsStoragePluginModule.forRoot({
      key: [
        ...STATE_LOCAL_STORAGE_SAVED.map((s) => ({
          key: s,
          engine: LOCAL_STORAGE_ENGINE,
        })),
        {
          key: UserSportsState,
          engine: LOCAL_STORAGE_ENGINE,
          // engine: SESSION_STORAGE_ENGINE
        },
        // {
        //   key: MyGroupsState,
        //   engine: SESSION_STORAGE_ENGINE
        // }
        {
          key: 'filterEvents.location',
          engine: LOCAL_STORAGE_ENGINE,
        },
        {
          key: 'filterEvents.radius',
          engine: LOCAL_STORAGE_ENGINE,
        },
      ],
    }),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    IonicStorageModule.forRoot(),
  ],
  providers: [
    LanguageInterceptor,
    // The guard asks whether a sign-up is in progress; this is what answers.
    // Without it the question has no answer, and creating an account with an
    // email address ends on the welcome screen.
    // SENTRY
    {
      provide: ErrorHandler,
      // Attach the Sentry ErrorHandler
      useValue: SentryAngular.createErrorHandler(),
    },
    {
      provide: SentryAngular.TraceService,
      deps: [Router],
    },
    // Does nothing but create both services at start-up, as the APP_INITIALIZER
    // it replaces did through its deps.
    provideAppInitializer(() => {
      inject(SentryAngular.TraceService);
      inject(LanguageService);
    }),
    // END SENTRY
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: Configuration,
      useFactory: (authService: JWTService) =>
        new Configuration({
          basePath: environment.api,
          // `?? ''`: there is no token before anyone signs in, and the
          // generated client only sets the Authorization header when the
          // credential is truthy -- which is what null did too.
          accessToken: () => authService.getAccessToken() ?? '',
        }),
      deps: [JWTService],
      multi: false,
    },
    {
      provide: BASE_PATH,
      useFactory: apiInitializer,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: LanguageInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: AppVersionInterceptor,
      multi: true,
    },
    {
      provide: NOTIFICATIONS_REPOSITORY,
      useClass: NotificationsRepositoryImpl,
    },
    CameraAdapter,
    PushNotificationAdapter,
    StripeAdapter,
    LocationAccuracy,
    {
      provide: FirebaseService,
      useValue: FirebaseFactory(!Capacitor.isNativePlatform(), { firebaseConfig: environment.firebaseConfig }),
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor() {
    console.log('Using the following API', environment.api);
  }
}
